import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  NavigationBar,
  ProfileMenu,
} from '@barracuda-internal/bds-core';
import {
  Dashboard,
  InfoOutlined,
  Key,
  LightBulbAuto,
  LogOut,
  OpenInNew,
  Schedule,
  Settings,
  User,
  UserGroup,
} from '@barracuda-internal/bds-core/dist/Icons/Core';
import SvgFaviconApp from '@barracuda-internal/bds-core/dist/Logos/Favicons/FaviconApp';
import { actions as authActions } from 'guardian/comp/login/authReducer';
import { useThemeMode } from 'common/ThemeMode';

const generateInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Menu shapes per effective role.
const buildRoutes = ({ role }) => {
  if (role === 'account_admin') {
    return [
      { path: '/', icon: <Dashboard />, name: 'Dashboard' },
      { path: '/scans', icon: <Schedule />, name: 'Scan History' },
      { path: '/api-keys', icon: <Key />, name: 'API Keys' },
      { path: '/settings/members', icon: <UserGroup />, name: 'Members' },
      { path: '/settings', icon: <Settings />, name: 'Settings' },
    ];
  }
  if (role === 'account_member') {
    return [
      { path: '/', icon: <Dashboard />, name: 'Dashboard' },
      { path: '/scans', icon: <Schedule />, name: 'Scan History' },
      // Read-only members list. Backend returns 403 on PATCH for
      // non-admins so the dropdown is hidden / disabled in the UI.
      { path: '/settings/members', icon: <UserGroup />, name: 'Members' },
    ];
  }
  // account_viewer: strictly read-only
  return [
    { path: '/', icon: <Dashboard />, name: 'Dashboard' },
    { path: '/scans', icon: <Schedule />, name: 'Scan History' },
    { path: '/settings/members', icon: <UserGroup />, name: 'Members' },
  ];
};

const NavigationPanel = ({ email, name }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const account = useSelector((state) => state.guardian.auth.account);
  const role = useSelector((state) => state.guardian.auth.role);
  const routes = buildRoutes({ role });
  const { mode, toggle: toggleTheme } = useThemeMode();
  const isDark = mode === 'dark';

  const handleLogout = () => {
    dispatch(authActions.logout());
    history.push('/login');
  };

  return (
    <NavigationBar
      title="Prompt Security"
      logo={<SvgFaviconApp />}
      options={[
        {
          name: 'Profile',
          initials: generateInitials(name),
          menu: (
            <ProfileMenu
              initials={generateInitials(name)}
              name={name}
              email={email}
              menuItems={[
                {
                  name: 'Manage account',
                  href: 'https://login.barracudanetworks.com/',
                  openInNewTab: true,
                  icon: <User />,
                  endIcon: <OpenInNew />,
                },
                {
                  name: isDark ? 'Switch to light mode' : 'Switch to dark mode',
                  onClick: toggleTheme,
                  icon: <LightBulbAuto />,
                },
                {
                  name: 'Privacy Policy',
                  href: 'https://www.barracuda.com/legal/privacy',
                  openInNewTab: true,
                  icon: <InfoOutlined />,
                },
                {
                  name: 'Sign Out',
                  onClick: handleLogout,
                  icon: <LogOut />,
                },
              ]}
              footer={
                account
                  ? {
                      serial: account.name,
                      version: account.tags?.join(', ') || 'v1.0',
                    }
                  : undefined
              }
            />
          ),
        },
      ]}
      routes={routes}
      currentPath={location.pathname}
      onNavigate={(path) => {
        history.push(path);
      }}
    />
  );
};

export default NavigationPanel;
